import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface InstallPromptProps {
  className?: string;
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const InstallPrompt: React.FC<InstallPromptProps> = ({ className = '' }) => {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);

  // Check if app was installed from PWA
  useEffect(() => {
    // Check if the app was launched as a PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check URL params for installation tracking
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('installed') === 'true') {
      setIsInstalled(true);
      // Remove the param to keep the URL clean
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('installed');
      window.history.replaceState({}, document.title, newUrl.toString());
    }

    // Check if user has dismissed the install prompt before
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) {
      setInstallDismissed(true);
    }
  }, []);

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent default behavior to show custom UI
      e.preventDefault();
      // Store the event for later use
      setInstallPromptEvent(e as BeforeInstallPromptEvent);
      // Show our installable UI
      setIsInstallable(true);
      // Track the event
      trackInstallEvent('prompt-shown');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      trackInstallEvent('installed');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle install button click
  const handleInstallClick = async () => {
    if (!installPromptEvent) return;

    trackInstallEvent('install-clicked');

    try {
      // Show the installation prompt
      await installPromptEvent.prompt();

      // Wait for the user's choice
      const choiceResult = await installPromptEvent.userChoice;

      // Clear the install prompt after user action
      setInstallPromptEvent(null);

      if (choiceResult.outcome === 'accepted') {
        setIsInstalled(true);
        trackInstallEvent('install-accepted');
      } else {
        trackInstallEvent('install-dismissed');
      }
    } catch (error) {
      console.error('Error during installation:', error);
      trackInstallEvent('install-error');
    }
  };

  // Handle dismiss button click
  const handleDismissClick = () => {
    setInstallDismissed(true);
    localStorage.setItem('installPromptDismissed', 'true');
    trackInstallEvent('prompt-dismissed');
  };

  // Track installation events for analytics
  const trackInstallEvent = (action: string) => {
    // Simple console logging for now
    console.log(`[PWA Install] ${action}`);

    // In a real app, you would send this to your analytics service
    // Example:
    // fetch('/api/v1/analytics', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     event: 'pwa_install',
    //     action,
    //     timestamp: new Date().toISOString()
    //   }),
    // }).catch(err => console.error('Failed to track install event:', err));
  };

  // Don't render anything if the app is already installed or the prompt was dismissed
  if (isInstalled || installDismissed || !isInstallable) {
    return null;
  }

  return (
    <AnimatePresence>
      {isInstallable && (
        <motion.div
          className={`${className} bg-white dark:bg-neutral-800 rounded-xl shadow-lg overflow-hidden`}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{
            type: "spring",
            damping: 25,
            stiffness: 300
          }}
        >
          <div className="p-5">
            <div className="flex items-start">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-1">
                  Install Museum App
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                  Install this app on your device for a better experience and offline access to exhibits.
                </p>
              </div>

              {/* App icon */}
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-blue-100 dark:bg-blue-900/50 ml-4 shrink-0 shadow-sm">
                <img
                  src="/images/logo.png"
                  alt="SK Museum Logo"
                  className="w-full h-full object-cover p-2"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-2">
              <motion.button
                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors"
                onClick={handleInstallClick}
                whileTap={{ scale: 0.95 }}
              >
                Install
              </motion.button>
              <motion.button
                className="py-2.5 px-4 bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-200 font-medium rounded-lg text-sm transition-colors"
                onClick={handleDismissClick}
                whileTap={{ scale: 0.95 }}
              >
                Not Now
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
